import { useState } from "react";
import { ChevronDown, ChevronRight, Fuel, Database, TrendingUp, Layers, Calendar, Filter } from "lucide-react";

const FuelConsumptionOverTime = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      category: "Source Data Fields",
      items: [
        {
          name: "fuelConsumption",
          source: "VesselMainEngineData",
          description: "Total fuel consumption in metric tons",
          calculation: "Sum of HFO + LFO + MGO from CSV import"
        },
        {
          name: "vesselStateTransition",
          source: "VesselMainEngineData",
          description: "Vessel operational state",
          calculation: "Direct value from CSV field"
        },
        {
          name: "engineId",
          source: "VesselMainEngineData",
          description: "Engine identifier",
          calculation: "Pipe-separated values from CSV"
        },
        {
          name: "date",
          source: "VesselMainEngineData",
          description: "Record timestamp",
          calculation: "Parsed from CSV date field"
        }
      ]
    },
    {
      category: "Aggregation Fields",
      items: [
        {
          name: "consumption",
          source: "Calculated",
          description: "Daily average fuel consumption",
          calculation: "Average of fuelConsumption per day per state"
        },
        {
          name: "state",
          source: "Grouped",
          description: "Vessel operational state",
          calculation: "Grouped from vesselStateTransition"
        },
        {
          name: "dataPointCount",
          source: "Calculated",
          description: "Number of records",
          calculation: "Count of records per day per state"
        }
      ]
    }
  ];

  const calculations = [
    {
      title: "Daily Consumption Average",
      formula: "∑ fuelConsumption ÷ count(records)",
      description: "Average fuel consumption for each day-state combination",
      parameters: ["Grouped by date", "Grouped by vessel state"],
      precision: "Rounded to 1 decimal place (0.1 MT)"
    },
    {
      title: "Engine Filtering",
      formula: "engineId matches pattern",
      description: "Filter for specific engine using regex pattern",
      parameters: ["Supports pipe-separated engine IDs", "Case-insensitive"],
      precision: "Exact or pipe-separated match"
    },
  ];

  const responseStructure = [
    {
      field: "date",
      type: "string",
      format: "YYYY-MM-DD",
      description: "Calendar date",
      example: "2024-01-15"
    },
    {
      field: "consumption",
      type: "number",
      format: "MT",
      description: "Average daily fuel consumption",
      example: "12.5"
    },
    {
      field: "state",
      type: "string",
      format: "text",
      description: "Vessel operational state",
      example: "underway"
    },
    {
      field: "dataPointCount",
      type: "integer",
      format: "count",
      description: "Number of source records",
      example: "8"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Fuel className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fuel Consumption Over Time
              </h2>
              <p className="text-gray-600 text-sm">
                Daily fuel consumption grouped by vessel operational state
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            
            {/* Data Sources */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Sources & Fields
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dataSources.map((source, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-3">{source.category}</h4>
                    <div className="space-y-3">
                      {source.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-gray-800">{item.name}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {item.source}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{item.description}</p>
                          <p className="text-xs text-gray-500">Calculation: {item.calculation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculations */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Core Calculations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {calculations.map((calc, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Layers className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{calc.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{calc.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                      <p className="text-sm font-mono bg-gray-50 p-2 rounded border">
                        {calc.formula}
                      </p>
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">Parameters:</p>
                      <div className="flex flex-wrap gap-1">
                        {calc.parameters.map((param, paramIdx) => (
                          <span key={paramIdx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {param}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Precision:</p>
                      <p className="text-xs text-gray-600">{calc.precision}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelConsumptionOverTime;